import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing, Dimensions } from '@/shared/theme';
import { Header } from './Header';
import { ScrollToTop } from './ScrollToTop';
import RegistrationPhaseRail from './RegistrationPhaseRail';
import { PhaseProgressBar } from './PhaseProgressBar';
import { phaseForRoute } from '@/consts/phases';
import { calculateFlowProgress } from '@/hooks/useAppFlow';
import { useRegistration } from '@/context/RegistrationContext';

// WCAG 2.4.1: Main landmark for skip link target. In frame mode this is a
// transparent flex pass-through (Boris `.reg-dt-main`) so the fixed height
// MainScroll provides reaches all the way down to FlowMain/Outlet: it holds
// no scroll or padding of its own.
const MainContent = styled.main<{ $frame: boolean }>`
  width: 100%;

  /* Remove focus outline - this element receives programmatic focus for accessibility */
  &:focus {
    outline: none;
  }

  @media (min-width: 900px) {
    ${({ $frame }) =>
      $frame &&
      `
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    `}
  }
`;

// Boris's per-screen "STEP X OF 4" phase bar (RPhaseBar) at the top of the main
// column. Rendered on both breakpoints — on mobile it replaces the legacy
// segmented header step bar (which Header now suppresses on flow steps) with
// Boris's continuous bar; the "STEP X OF 4" label itself is desktop-only (hidden
// on mobile inside PhaseProgressBar). Shown on flow steps but not the screeners
// (which render their own per-question dashes).
const PhaseBar = styled.div`
  display: block;
  margin-bottom: ${Spacing.x3}px;
`;

// Boris caps the main reading column at 620px (register.css .reg-dt-main .rg-inner);
// only flow steps are capped — landing / terminal screens stay full-width.
// Also doubles as the fallback scroller (register.css's base `.rg-body`) for
// steps that haven't yet migrated onto their own RgBody/RgFoot split (Phase 2
// work): a migrated step's own root fills this box via flex:1/min-height:0,
// which keeps this element's own overflow dormant so only the step's inner
// RgBody actually scrolls: for a not-yet-migrated step, this is the same
// scroll boundary MainScroll used to provide, just moved one level down.
//
// Widened by 2×Spacing.x0_5 (620 -> 628) to offset RgBody's new horizontal
// padding (borisFields.tsx) 1:1, so the visible field width stays exactly
// 620px's worth — the extra 8px only exists to give focus-ring box-shadows
// clearance before RgBody's overflow-x:hidden clips them.
const FlowMain = styled.div<{ $cap: boolean }>`
  width: 100%;
  ${({ $cap }) => ($cap ? `max-width: ${620 + Spacing.x0_5 * 2}px; margin: 0 auto;` : '')}
  @media (min-width: 900px) {
    ${({ $cap }) =>
      $cap &&
      `
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      /* Boris .reg-dt-main .rg-inner: 44px top / 56px sides / 32px bottom. */
      padding: 44px 56px 32px;
    `}
  }
`;

// Boris's desktop registration is a single-viewport frame (register.css .reg-dt /
// .reg-dt-cols): the top bar + a fixed 2-column body that fills the rest of the
// screen. The left photo rail is pinned (never scrolls); only the main column
// scrolls. On rail routes at desktop we replicate that so the rail photo stays
// put and the form scrolls inside its own column (no page scroll, no gap). On
// mobile / non-rail routes this is a no-op and the page scrolls normally.
const Shell = styled.div<{ $frame: boolean }>`
  /* #root is a centered flex column (align-items: center), so this wrapper must
     claim the full width or it shrinks to its content and leaves side gutters. */
  width: 100%;
  @media (min-width: 900px) {
    ${({ $frame }) =>
      $frame &&
      `
      height: 100dvh;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    `}
  }
`;

// The main column is a pure flex-column HOST in frame mode (Boris `.rg-screen`
// filling `.reg-dt-main`): it no longer scrolls or pads itself. Scrolling and
// the 44/56/32 `.rg-inner` padding move to FlowMain (the fallback scroller for
// steps not yet migrated onto their own RgBody/RgFoot split), so a migrated
// step's own RgFoot can sit outside any scrolling ancestor as a true pinned
// flex sibling instead of a `position:sticky` element inside one.
const MainScroll = styled.div<{ $frame: boolean }>`
  width: 100%;
  min-width: 0;
  @media (min-width: 900px) {
    ${({ $frame }) =>
      $frame &&
      `
      height: 100%;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow-x: hidden;
    `}
  }
`;

// Responsive two-column shell: a desktop-only left phase rail + main content
// column. `$showRail` also gates the grid-template-columns (not just the
// Aside's own display), so non-flow routes stay a single full-width column
// at any viewport instead of leaving the main column squeezed into the
// rail's track with the second track empty.
const FlowCols = styled.div<{ $showRail: boolean }>`
  width: 100%;
  max-width: ${Dimensions.gridWidth}px;
  margin: 0 auto;
  padding: 0 ${Spacing.x2}px ${Spacing.x3}px;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Spacing.x3}px;
  @media (min-width: 900px) {
    align-items: stretch;
    /* Rail routes go full-bleed: Boris's aside is a wide photo panel flush to the
       left edge, not a narrow card inside the centered 1000px grid. Drop the
       max-width/side padding so the rail fills the left column edge-to-edge; the
       main column keeps its readable measure via FlowMain's 620px cap. */
    ${({ $showRail }) =>
      $showRail
        ? `
      flex: 1;
      min-height: 0;
      overflow: hidden;
      max-width: none;
      margin: 0;
      padding: 0;
      gap: ${Spacing.x4}px;
      grid-template-columns: minmax(380px, 460px) minmax(0, 1fr);
    `
        : `
      grid-template-columns: 1fr;
    `}
  }
`;

// The $show-driven toggle must be the unconditional declaration (not nested
// inside the min-width media query): jsdom's getComputedStyle does not
// evaluate @media blocks at all (verified directly against jsdom - it always
// resolves to the base rule regardless of window width), so a11y-role-based
// tests would never see the rail if "display: block" only existed inside a
// media query. The max-width query force-hides on mobile regardless of
// $show, which real browsers (unlike jsdom) do correctly evaluate.
const Aside = styled.div<{ $show: boolean }>`
  display: ${({ $show }) => ($show ? 'block' : 'none')};
  @media (max-width: 899px) {
    display: none;
  }
  /* Rail stretches to the full grid-row height so the photo always fills the
     left column with no empty gap below it. RegistrationPhaseRail floors itself
     to the viewport height, so on short screens the panel still reaches the
     bottom of the screen. */
  @media (min-width: 900px) {
    align-self: stretch;
  }
`;

// Pages that don't show progress bar (post-registration pages or terminal
// states). `/eligibility-result` is grouped in here too: it's a transient
// polling/redirect screen, not a form step, so the desktop rail + "STEP n OF 4"
// bar it would otherwise get (phases.ts maps it to phase 2) has nothing to
// anchor to: the splash just clumps at the top of an empty cream column. As a
// plain centered screen (like the other terminals here) it matches Boris's
// .rg-proc/.rg-result, which are never framed by the rail either.
const POST_REGISTRATION_PATHS = ['/check-sms', '/download-app-qr', '/not-eligible', '/cap-reached', '/email-sent', '/eligibility-result'];

// Screener steps render their own per-question dash progress (QuestionProgress),
// so the whole-flow header progress bar is hidden on these paths.
const SCREENER_PATHS = ['/phq9-screener', '/gad7-screener'];

// Steps migrated onto their own RgBody/RgFoot split (Phase 1c) render the
// phase bar themselves, at the top of their own RgBody, so it scrolls away
// with the rest of their content instead of freezing in place above it (their
// own root now fills FlowMain via flex, which leaves FlowMain's own scroll
// dormant, so a phase bar rendered here, outside that root, would no longer
// scroll at all). Suppress Layout's copy on these paths to avoid a duplicate.
const STEP_OWNED_PHASE_BAR_PATHS = [
  '/medicare-coverage',
  '/shipping',
  '/health-conditions',
  '/ckm-conditions',
  '/behavioral-health-conditions',
];

// WCAG 2.4.2: Page titles for each step
const PAGE_TITLES: Record<string, string> = {
  '/': 'Welcome',
  '/medicare-coverage': 'Medicare Coverage',
  '/health-conditions': 'Health Conditions',
  '/ckm-conditions': 'Health Conditions',
  '/behavioral-health-conditions': 'Behavioral Health',
  '/phq9-screener': 'Mental Health Check-in',
  '/gad7-screener': 'Mental Health Check-in',
  '/track-offering': 'Your Program',
  '/not-eligible': 'Not Eligible',
  '/create-account': 'Create Account',
  '/download-app': 'Download App',
  '/check-sms': 'Check SMS',
  '/download-app-qr': 'Download App QR Code',
};

const Layout = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const registrationContext = useRegistration();
  const mainRef = useRef<HTMLElement>(null);
  const mainScrollRef = useRef<HTMLDivElement>(null);

  // Don't show progress on landing page or post-registration pages
  const isPostRegistration = POST_REGISTRATION_PATHS.includes(pathname);
  // The screener screens show their own per-question progress instead of the flow bar.
  const isScreener = SCREENER_PATHS.includes(pathname);
  const showProgress = pathname !== '/' && !isPostRegistration && !isScreener;

  // Flow-aware progress (excludes steps not required for this user, e.g. the
  // BH screeners when no BH condition was reported) instead of a fixed
  // route-index calc, which would jump when a conditional step is skipped.
  const { current: currentStep, total: totalSteps, percentage: progress } =
    calculateFlowProgress(registrationContext);

  // Desktop phase rail: only shown on registration flow steps (Task 1/2), and
  // never on post-registration/terminal screens — those intentionally hide
  // the header progress bar too, so the two signals shouldn't disagree.
  const currentPhase = phaseForRoute(pathname);
  const showRail = currentPhase >= 0 && !isPostRegistration;
  const hasOwnPhaseBar = STEP_OWNED_PHASE_BAR_PATHS.includes(pathname);

  // WCAG 2.4.2: Update page title on navigation
  useEffect(() => {
    const pageTitle = PAGE_TITLES[pathname] || 'Registration';
    document.title = `${pageTitle} - Happy Hearts`;
  }, [pathname]);

  // WCAG 3.1.2: Update lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // WCAG 2.4.3: Focus management on page transitions
  useEffect(() => {
    // Focus the main content area on navigation for screen readers
    if (mainRef.current) {
      mainRef.current.focus();
    }
    // In frame mode FlowMain is the fallback scroller (a migrated step's own
    // RgBody is a fresh DOM node on every mount, so it never needs an explicit
    // reset), so reset IT to the top on each step change; the window itself
    // does not scroll in frame mode.
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
  }, [pathname]);

  return (
    <ScrollToTop>
      <Shell $frame={showRail}>
        <Header
          showDivider={showProgress}
          progress={progress}
          currentStep={currentStep}
          totalSteps={totalSteps}
          showLogo={true}
          hasPhaseRail={showRail}
        />
        <FlowCols $showRail={showRail}>
          <Aside $show={showRail}>
            {showRail && <RegistrationPhaseRail currentPhase={currentPhase} />}
          </Aside>
          <MainScroll $frame={showRail}>
            <MainContent
              id="main-content"
              ref={mainRef}
              tabIndex={-1}
              aria-label="Main content"
              $frame={showRail}
            >
              <FlowMain $cap={showRail} ref={mainScrollRef}>
                {showRail && !isScreener && !hasOwnPhaseBar && (
                  <PhaseBar>
                    <PhaseProgressBar phase={currentPhase} />
                  </PhaseBar>
                )}
                <Outlet />
              </FlowMain>
            </MainContent>
          </MainScroll>
        </FlowCols>
      </Shell>
    </ScrollToTop>
  );
};

export { Layout };
